import React, {useId, useLayoutEffect, useRef, useState} from "react";
import {Button, Table} from "reactstrap";
import {formatMoney, formatPercent} from "../../../shared/utils/format";
import {axis, monthLabel, monthNumeric} from "../utils/chartScale";
import {direction, linearTrend} from "../utils/trend";
import InfoTip from "../../../shared/components/InfoTip";
import "./MonthChart.css";
import IconLabel from "../../../shared/components/IconLabel";
import {ChartIcon, TableIcon} from "../../../shared/components/icons";

const HEIGHT = 300;
const FALLBACK_WIDTH = 720; // when the width can't be measured
// Axis numbers are plain figures (1.000, 2.000), not words ("1 mil").
const AMOUNT = new Intl.NumberFormat("pt-BR", {maximumFractionDigits: 2});
const WHOLE = new Intl.NumberFormat("pt-BR", {maximumFractionDigits: 0});
const CHARACTER_WIDTH = 7; // pixels a digit takes in the 12px axis labels

/** The width of an element, kept up to date as it is resized. */
const useWidth = (/** @type {React.RefObject<HTMLElement|null>} */ ref) => {
    const [width, setWidth] = useState(FALLBACK_WIDTH);
    useLayoutEffect(() => {
        const element = ref.current;
        if (!element) return undefined;
        const measure = () => setWidth(element.getBoundingClientRect().width || FALLBACK_WIDTH);
        measure();
        if (typeof ResizeObserver === "undefined") return undefined;
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        return () => observer.disconnect();
    }, [ref]);
    return width;
};

/**
 * @typedef {{key: string, label: string, tone: string, value: (month: any) => number|null}} ChartSeries
 *   One line (or one layer of the bars): `tone` picks its color (`--chart-<tone>`), `value` its number for a
 *   month, or null when there is none (a month without paid orders has no ticket).
 * @typedef {{header: string, cell: (month: any) => string, total?: string}} TableColumn
 *   One column of the table view, after the month's own; `total` is its figure in the last row, if any.
 */

/**
 * A figure per month, as lines or as bars, for the last twelve months: one
 * chart answering one question (revenue and profit, orders, margin...).
 *
 * The axis starts at zero (a value below it is drawn on the zero line; the
 * readout and the table have the real figure). Hovering, or the arrow keys
 * once the chart has focus, shows a crosshair and a readout of the month;
 * the same numbers are in the table view. The last month is still going on,
 * so its line segment is dashed and its bar paler. With several series, bars
 * are stacked.
 *
 * With `trend`, a dotted straight line shows where each line is heading (or,
 * with bars, where the total is): fitted through the months that are over
 * (see `utils/trend.js`), with how fast it moves in words beside the title.
 * (Not in the readout or the table, which keep to the real figures.) Too few
 * months, no trend.
 *
 * What is on its way (money not paid yet) is not drawn: it can be told in the
 * readout of the month in progress (`expectedNote`), where it is next to the
 * real figures without being taken for one.
 *
 * @param {{
 *   title: string,
 *   subtitle?: string,
 *   headline?: React.ReactNode,
 *   months: {month: string}[]|null,
 *   series: ChartSeries[],
 *   kind?: "lines"|"bars",
 *   unit?: "money"|"whole"|"percent",
 *   currency?: string,
 *   error?: boolean,
 *   emptyMessage: string,
 *   ariaLabel: string,
 *   columns: TableColumn[],
 *   expectedNote?: React.ReactNode,
 *   trend?: boolean,
 * }} props `months` is null while loading
 */
const MonthChart = ({
    title,
    subtitle,
    headline,
    months,
    series,
    kind = "lines",
    unit = "whole",
    currency = "",
    error = false,
    emptyMessage,
    ariaLabel,
    columns,
    expectedNote,
    trend = false,
}) => {
    const wrapperRef = useRef(/** @type {HTMLDivElement|null} */ (null));
    const width = useWidth(wrapperRef);
    const [active, setActive] = useState(/** @type {number|null} */ (null));
    const [asTable, setAsTable] = useState(false);
    const titleId = useId();

    const list = months ?? [];
    const hasData = list.some((m) => series.some((s) => (s.value(m) ?? 0) > 0));
    const format = (/** @type {number} */ value) =>
        unit === "money"
            ? formatMoney(value, currency)
            : unit === "percent"
              ? formatPercent(value)
              : WHOLE.format(value);

    // The trend of each line, or (bars) of the stack's total: a name, a color and the fitted line.
    const trends = trend && months ? trendsOf(list, series, kind) : [];
    let body;
    if (error) {
        body = <p className="month-chart__note">Não foi possível carregar o gráfico.</p>;
    } else if (!months) {
        body = <p className="month-chart__note">Carregando…</p>;
    } else if (!hasData) {
        body = <p className="month-chart__note">{emptyMessage}</p>;
    } else if (asTable) {
        body = <DataTable months={list} columns={columns} />;
    } else {
        body = (
            <Plot
                months={list}
                series={series}
                kind={kind}
                unit={unit}
                currency={currency}
                format={format}
                expectedNote={expectedNote}
                trends={trends}
                ariaLabel={ariaLabel}
                width={width}
                active={active}
                setActive={setActive}
            />
        );
    }

    return (
        <section className="month-chart" aria-labelledby={titleId}>
            <div className="month-chart__header">
                <div>
                    <div className="month-chart__titleline">
                        <h5 id={titleId} className="mb-0">
                            {title}
                        </h5>
                        {months && hasData && headline && <p className="month-chart__totals">{headline}</p>}
                    </div>
                    {subtitle && <p className="month-chart__subtitle">{subtitle}</p>}
                    {months && hasData && <TrendNote trends={trends} />}
                </div>
                {months && hasData && (
                    <Button
                        color="link"
                        size="sm"
                        className="p-0 text-nowrap flex-shrink-0"
                        onClick={() => setAsTable((t) => !t)}
                    >
                        {asTable ? (
                            <IconLabel icon={<ChartIcon />}>Ver gráfico</IconLabel>
                        ) : (
                            <IconLabel icon={<TableIcon />}>Ver tabela</IconLabel>
                        )}
                    </Button>
                )}
            </div>
            <div ref={wrapperRef}>{body}</div>
        </section>
    );
};

/**
 * The numbers behind the chart, for those who can't or would rather not read
 * it, and the totals of the whole period when the chart has any.
 */
const DataTable = ({months, columns}) => {
    const hasTotals = columns.some((column) => column.total !== undefined);
    return (
        <Table size="sm" responsive className="month-chart__table mb-0">
            <thead>
                <tr>
                    <th>Mês</th>
                    {columns.map((column) => (
                        <th className="text-end" key={column.header}>
                            {column.header}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {months.map((m, i) => (
                    <tr key={m.month}>
                        <td>
                            {monthNumeric(m.month)}
                            {i === months.length - 1 && <span className="text-muted"> (em andamento)</span>}
                        </td>
                        {columns.map((column) => (
                            <td className="text-end" key={column.header}>
                                {column.cell(m)}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
            {hasTotals && (
                <tfoot>
                    <tr>
                        <th scope="row">Total</th>
                        {columns.map((column) => (
                            <td className="text-end" key={column.header}>
                                {column.total ?? ""}
                            </td>
                        ))}
                    </tr>
                </tfoot>
            )}
        </Table>
    );
};

/**
 * @typedef {{key: string, label: string, tone: string, fit: NonNullable<ReturnType<typeof linearTrend>>}} Trend
 */

/**
 * The trends to show: one per line, or one for the total of stacked bars.
 * Series without enough months to go on have none.
 * @param {{month: string}[]} months
 * @param {ChartSeries[]} series
 * @param {"lines"|"bars"} kind
 * @returns {Trend[]}
 */
const trendsOf = (months, series, kind) => {
    if (kind === "bars") {
        const totals = months.map((m) => {
            const known = series.map((s) => s.value(m)).filter((v) => v !== null);
            return known.length ? known.reduce((sum, v) => sum + Math.max(v, 0), 0) : null;
        });
        const fit = linearTrend(totals);
        return fit ? [{key: "total", label: series.length > 1 ? "Total" : series[0].label, tone: "neutral", fit}] : [];
    }
    return series.flatMap((s) => {
        const fit = linearTrend(months.map((m) => s.value(m)));
        return fit ? [{key: s.key, label: s.label, tone: s.tone, fit}] : [];
    });
};

const ARROWS = {up: "▲", down: "▼", flat: "▶"};

/** How fast each trend moves, in words and an arrow, under the chart's subtitle. */
const TrendNote = ({trends}) => {
    const shown = trends.filter((t) => t.fit.percent !== null);
    if (!shown.length) return null;
    return (
        <p className="month-chart__trends">
            {shown.map((t) => {
                const percent = /** @type {number} */ (t.fit.percent);
                const way = direction(percent);
                const words =
                    way === "flat"
                        ? "estável"
                        : `${way === "up" ? "subindo" : "caindo"} ${formatPercent(Math.abs(percent))} ao mês`;
                return (
                    <span className={`month-chart__trend-note month-chart__trend-note--${way}`} key={t.key}>
                        <span aria-hidden="true">{ARROWS[way]}</span>{" "}
                        {shown.length > 1 && <span className="month-chart__trend-name">{t.label}: </span>}
                        {words}
                    </span>
                );
            })}
            <InfoTip>
                A tendência é uma reta ajustada aos meses já fechados (sem o mês em andamento): mostra para onde a linha
                caminha e quanto muda por mês, em relação à média.
            </InfoTip>
        </p>
    );
};

const Plot = ({
    months,
    series,
    kind,
    unit,
    currency,
    format,
    expectedNote,
    trends,
    ariaLabel,
    width,
    active,
    setActive,
}) => {
    const clipId = useId();
    const svgRef = useRef(/** @type {SVGSVGElement|null} */ (null));
    const narrow = width < 480;
    const last = months.length - 1;
    const stacked = kind === "bars";

    // values[s][i]: what series s says for month i (null: nothing to draw)
    /** @type {(number|null)[][]} */
    const values = series.map((s) => months.map((m) => s.value(m)));
    const shown = (/** @type {number|null} */ v) => (v === null ? 0 : Math.max(v, 0));
    const tops = months.map((_, i) =>
        stacked ? values.reduce((sum, row) => sum + shown(row[i]), 0) : Math.max(...values.map((row) => shown(row[i])))
    );
    const highest = Math.max(...tops);
    const scale = axis(highest, unit === "whole");
    const ticks = Array.from({length: scale.intervals + 1}, (_, i) => i);
    const tickLabels = ticks.map((t) => {
        const text = (unit === "whole" ? WHOLE : AMOUNT).format(scale.step * t);
        return unit === "percent" ? `${text}%` : text;
    });
    // The margin makes room for the widest number on the axis (10.000 needs more than 500).
    const left = Math.max(narrow ? 44 : 60, Math.max(...tickLabels.map((l) => l.length)) * CHARACTER_WIDTH + 16);
    const margin = {top: 44, right: 16, bottom: 34, left};
    const plotWidth = Math.max(width - margin.left - margin.right, 1);
    const plotHeight = HEIGHT - margin.top - margin.bottom;
    const slot = plotWidth / months.length;

    const x = (/** @type {number} */ i) => margin.left + slot * (i + 0.5);
    const y = (/** @type {number} */ v) => margin.top + plotHeight * (1 - Math.max(v, 0) / scale.max);
    // The trend lines are not clamped (their slope has to be true): the clip below trims what leaves the plot.
    const yTrend = (/** @type {number} */ v) => margin.top + plotHeight * (1 - v / scale.max);

    // Every month's name if they fit, otherwise every other one (and always the last).
    const every = slot >= 46 ? 1 : 2;
    const labelled = (/** @type {number} */ i) => (last - i) % every === 0;

    // The legend: a key and a name per series, one after the other.
    let legendX = 0;
    const legend = series.map((s, i) => {
        const text = unit === "money" && i === 0 ? `${s.label} (${currency})` : s.label;
        const item = {key: s.key, tone: s.tone, text, x: legendX};
        legendX += 22 + text.length * 7 + 18;
        return item;
    });

    const indexAt = (/** @type {number} */ clientX) => {
        const origin = svgRef.current?.getBoundingClientRect().left ?? 0;
        return Math.min(Math.max(Math.round((clientX - origin - margin.left) / slot - 0.5), 0), last);
    };
    const onKeyDown = (/** @type {React.KeyboardEvent} */ event) => {
        const moves = {ArrowLeft: -1, ArrowRight: 1};
        if (event.key in moves) {
            event.preventDefault();
            setActive((a) => Math.min(Math.max((a ?? last) + moves[event.key], 0), last));
        } else if (event.key === "Home") {
            event.preventDefault();
            setActive(0);
        } else if (event.key === "End") {
            event.preventDefault();
            setActive(last);
        } else if (event.key === "Escape") {
            setActive(null);
        }
    };

    const month = active === null ? null : months[active];
    // The readout sits beside the crosshair, on whichever side has the room.
    const readoutOnLeft = active !== null && x(active) > width * 0.6;
    const barWidth = Math.min(slot * 0.6, 56);

    return (
        <div
            className="month-chart__plot"
            role="group"
            aria-label={`${ariaLabel}, de ${monthLabel(months[0].month)} a ${monthLabel(months[last].month)}. Use as setas para percorrer os meses, ou veja a tabela.`}
            tabIndex={0}
            onKeyDown={onKeyDown}
            onFocus={() => setActive((a) => a ?? last)}
            onBlur={() => setActive(null)}
            style={{width, height: HEIGHT}}
        >
            <svg ref={svgRef} width={width} height={HEIGHT} aria-hidden="true">
                {ticks.map((t) => {
                    const lineY = margin.top + plotHeight * (1 - t / scale.intervals);
                    return (
                        <g key={t}>
                            <line
                                className={t === 0 ? "month-chart__axis" : "month-chart__grid"}
                                x1={margin.left}
                                x2={width - margin.right}
                                y1={lineY}
                                y2={lineY}
                            />
                            <text
                                className="month-chart__tick"
                                x={margin.left - 8}
                                y={lineY}
                                textAnchor="end"
                                dy="0.32em"
                            >
                                {tickLabels[t]}
                            </text>
                        </g>
                    );
                })}

                {/* what each series is, keyed by the color it is drawn in */}
                <g>
                    {legend.map((item) => (
                        <g key={item.key}>
                            <line
                                className={`month-chart__key month-chart__tone-${item.tone}`}
                                x1={item.x}
                                x2={item.x + 16}
                                y1={16}
                                y2={16}
                            />
                            <text className="month-chart__axis-title" x={item.x + 22} y={16} dy="0.32em">
                                {item.text}
                            </text>
                        </g>
                    ))}
                </g>

                {months.map((m, i) =>
                    labelled(i) ? (
                        <text
                            key={m.month}
                            className="month-chart__tick"
                            x={x(i)}
                            y={HEIGHT - margin.bottom + 20}
                            textAnchor="middle"
                        >
                            {monthLabel(m.month)}
                        </text>
                    ) : null
                )}

                {active !== null && (
                    <line
                        className="month-chart__crosshair"
                        x1={x(active)}
                        x2={x(active)}
                        y1={margin.top}
                        y2={margin.top + plotHeight}
                    />
                )}

                {stacked &&
                    months.map((m, i) => {
                        let below = 0;
                        return (
                            <g key={m.month} className={i === last ? "month-chart__bar--ongoing" : undefined}>
                                {series.map((s, si) => {
                                    const value = shown(values[si][i]);
                                    const top = y(below + value);
                                    const bottom = y(below);
                                    below += value;
                                    return (
                                        <rect
                                            key={s.key}
                                            className={`month-chart__bar month-chart__tone-${s.tone}`}
                                            data-series={s.key}
                                            x={x(i) - barWidth / 2}
                                            y={top}
                                            width={barWidth}
                                            height={Math.max(bottom - top, 0)}
                                        />
                                    );
                                })}
                            </g>
                        );
                    })}

                {/* where each line is heading: dotted, under the figures, cut at the edges of the plot */}
                {trends.length > 0 && (
                    <>
                        <clipPath id={clipId}>
                            <rect x={margin.left} y={margin.top} width={plotWidth} height={plotHeight} />
                        </clipPath>
                        <g clipPath={`url(#${clipId})`}>
                            {trends.map((t) => (
                                <line
                                    key={t.key}
                                    className={`month-chart__trend month-chart__tone-${t.tone}`}
                                    data-trend={t.key}
                                    x1={x(0)}
                                    y1={yTrend(t.fit.at(0))}
                                    x2={x(last)}
                                    y2={yTrend(t.fit.at(last))}
                                />
                            ))}
                        </g>
                    </>
                )}

                {!stacked &&
                    series.map((s, si) => {
                        const row = values[si];
                        // runs of consecutive months with a figure, up to the month before the last one
                        /** @type {number[][][]} */
                        const runs = [];
                        let run = /** @type {number[][]} */ ([]);
                        row.slice(0, last).forEach((v, i) => {
                            if (v === null) {
                                if (run.length) runs.push(run);
                                run = [];
                            } else {
                                run.push([x(i), y(v)]);
                            }
                        });
                        if (run.length) runs.push(run);
                        const ongoing =
                            last > 0 && row[last - 1] !== null && row[last] !== null
                                ? [
                                      [x(last - 1), y(/** @type {number} */ (row[last - 1]))],
                                      [x(last), y(/** @type {number} */ (row[last]))],
                                  ]
                                : null;
                        const points = (/** @type {number[][]} */ p) => p.map(([px, py]) => `${px},${py}`).join(" ");
                        return (
                            <g key={s.key} className={`month-chart__series month-chart__tone-${s.tone}`}>
                                {runs
                                    .filter((r) => r.length > 1)
                                    .map((r) => (
                                        <polyline key={r[0][0]} className="month-chart__line" points={points(r)} />
                                    ))}
                                {ongoing && (
                                    <polyline
                                        className="month-chart__line month-chart__line--ongoing"
                                        points={points(ongoing)}
                                    />
                                )}
                                {row.map((v, i) =>
                                    v === null ? null : (
                                        <circle
                                            key={months[i].month}
                                            className="month-chart__marker"
                                            cx={x(i)}
                                            cy={y(v)}
                                            r={active === i ? 5.5 : 4}
                                        />
                                    )
                                )}
                            </g>
                        );
                    })}

                {/* the hit area is the whole plot, so a month is found by where the pointer is, not by aiming at a dot */}
                <rect
                    data-testid="month-chart-hit-area"
                    x={margin.left}
                    y={margin.top}
                    width={plotWidth}
                    height={plotHeight}
                    fill="transparent"
                    onPointerMove={(event) => setActive(indexAt(event.clientX))}
                    onPointerLeave={() => setActive(null)}
                />
            </svg>

            {month && active !== null && (
                <div
                    className="month-chart__readout"
                    role="status"
                    style={
                        readoutOnLeft
                            ? {right: width - x(active) + 12, top: margin.top}
                            : {left: x(active) + 12, top: margin.top}
                    }
                >
                    <div className="month-chart__readout-title">
                        {monthLabel(month.month)}
                        {active === last && <span> · em andamento</span>}
                    </div>
                    {series.map((s, si) => {
                        const value = values[si][active];
                        return (
                            <div className="month-chart__readout-row" key={s.key}>
                                <svg width="16" height="8" aria-hidden="true" className={`month-chart__tone-${s.tone}`}>
                                    <line className="month-chart__key" x1="0" x2="16" y1="4" y2="4" />
                                </svg>
                                <strong>{value === null ? "–" : format(value)}</strong> <span>{s.label}</span>
                            </div>
                        );
                    })}
                    {stacked && series.length > 1 && (
                        <div className="month-chart__readout-row month-chart__readout-total">
                            <strong>{format(values.reduce((sum, row) => sum + (row[active] ?? 0), 0))}</strong>{" "}
                            <span>total</span>
                        </div>
                    )}
                    {expectedNote && active === last && (
                        <div className="month-chart__readout-expected">{expectedNote}</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MonthChart;
